// File: typescript/apps/front-end/src/main/react/group/GroupRailLazy.tsx
//
// Lazy boundary for GroupRail. GroupRail uses @dnd-kit (~255 KB) for reordering, and GroupPage is
// statically mounted by ReactMounter, so without this wrapper @dnd-kit ships in main_bundle.js on
// EVERY page (homepage, discussion, user) even though only the group page reorders anything. Loading
// it through this wrapper keeps @dnd-kit in an on-demand chunk that loads when the group rail renders.
// Drop-in named replacement for `import { GroupRail }`; GroupPage only changes its import path.
// See designs/01-now/2026-06-08-frontend-bundle-code-splitting-implementation-plan.md (slice 4).

import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { RecoverableIslandFallback } from "@/main/react/RecoverableIslandFallback";

// Type-only import() query: erased at runtime (no @dnd-kit pull), gives the exact prop types.
type GroupRailProps = React.ComponentProps<(typeof import("@/main/react/group/GroupRail"))["GroupRail"]>;

const GroupRailImpl = lazyWithRetry(
    () => import("@/main/react/group/GroupRail").then((m) => ({ default: m.GroupRail })),
    "group-rail",
);

/**
 * The wait, shaped like what replaces it (`EMPTY-STATES-44`).
 *
 * <p>This used to be three generic blocks — `h-10` + `h-24` + `h-24` with `space-y-3`, so 40 + 96 + 96
 * + 2x12 = **256px** — against a measured **489px** first rail card. The chunk landing therefore grew
 * the column by ~233px under whatever the viewer was about to click, and on an empty group the rail IS
 * the page: banner, avatar, name, description, count chips, visibility, leadership and the primary
 * action all arrive at once.
 *
 * <p>The blocks below trace the real first card instead: banner strip, avatar over the name and byline,
 * two description lines, the action row, the count chips. The arithmetic, matching the `gap-4` the rail
 * itself uses between cards:
 *
 * <pre>
 *   card 1: 96 banner + 16 + 56 identity + 12 + 16 + 8 + 16 line + 12 + 36 action + 12 + 32 chips
 *           + 2x16 card padding + 2 border ~= 489
 * </pre>
 *
 * <p>It stays `aria-hidden` grey blocks rather than the real header. Mounting the real
 * `GroupDesktopRailHeader` here would put `GroupButtons` through a mount/unmount/remount cycle, and
 * that component consumes the `?join=1` and `?welcome=1` one-shot intents on mount — a duplicate-effect
 * risk a running app has to settle, not a worktree. See the batch-F design doc's X-class section.
 */
function GroupRailFallback() {
    return (
        <div className="flex w-full animate-pulse flex-col gap-4" aria-hidden="true">
            <div className="w-full overflow-hidden rounded-2xl border border-border/60 bg-card">
                <div className="h-24 w-full bg-muted" />
                <div className="flex flex-col gap-3 p-4">
                    <div className="flex items-center gap-3">
                        <div className="size-14 shrink-0 rounded-full bg-muted" />
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                            <div className="h-5 w-2/3 rounded bg-muted" />
                            <div className="h-4 w-1/2 rounded bg-muted" />
                        </div>
                    </div>
                    <div className="h-4 w-full rounded bg-muted" />
                    <div className="h-4 w-5/6 rounded bg-muted" />
                    <div className="h-9 w-full rounded-full bg-muted" />
                    <div className="flex flex-wrap gap-2">
                        <div className="h-8 w-24 rounded-full bg-muted" />
                        <div className="h-8 w-20 rounded-full bg-muted" />
                        <div className="h-8 w-24 rounded-full bg-muted" />
                    </div>
                </div>
            </div>
            <div className="h-32 w-full rounded-2xl border border-border/60 bg-card" />
            <div className="h-32 w-full rounded-2xl border border-border/60 bg-card" />
        </div>
    );
}

export function GroupRail(props: GroupRailProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs the rail, not the GroupPage
    // island. On an empty group the rail IS the page, so the retry card (not `null`, not the skeleton
    // forever) is what tells the member something to do; its Retry reloads, which is what recovers a
    // chunk that has since become available.
    return (
        <LazyControlBoundary controlName="group-rail" fallback={<RecoverableIslandFallback />}>
            <Suspense fallback={<GroupRailFallback />}>
                <GroupRailImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
